import { useState } from "react";
import { Plus, X } from "lucide-react";

/**
 * Generic editable list of structured rows used by Care Plan.
 *
 * `fields` is an array of column descriptors:
 *   [{ key: 'name', label: 'Medication', type: 'text', required: true, w: 'col-span-2' }]
 *
 * `value` is the list of row objects.
 * `onChange` receives the new list.
 */
export function StructuredListEditor({ value, onChange, fields, addLabel, emptyLabel, testidPrefix }) {
  const rows = Array.isArray(value) ? value : [];
  const setRow = (i, key, v) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r));
    onChange(next);
  };
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => {
    const empty = {};
    for (const f of fields) empty[f.key] = f.type === "checkbox" ? false : (f.type === "number" ? null : "");
    onChange([...rows, empty]);
  };
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
      {rows.length === 0 && <p className="text-xs text-slate-400 italic">{emptyLabel || "None recorded."}</p>}
      {rows.map((row, i) => (
        <div key={i} className="rounded border border-slate-200 p-2.5 bg-slate-50" data-testid={`${testidPrefix}-row-${i}`}>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            {fields.map((f) => {
              const v = row[f.key];
              if (f.type === "select") {
                return (
                  <label key={f.key} className={`text-[11px] text-slate-600 ${f.w || "md:col-span-2"}`}>
                    {f.label}
                    <select
                      value={v || ""}
                      onChange={(e) => setRow(i, f.key, e.target.value)}
                      data-testid={`${testidPrefix}-${i}-${f.key}`}
                      className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white"
                    >
                      <option value="">—</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                );
              }
              if (f.type === "checkbox") {
                return (
                  <label key={f.key} className={`text-xs text-slate-700 inline-flex items-center gap-1 mt-4 ${f.w || "md:col-span-1"}`}>
                    <input
                      type="checkbox"
                      checked={!!v}
                      onChange={(e) => setRow(i, f.key, e.target.checked)}
                      data-testid={`${testidPrefix}-${i}-${f.key}`}
                    />
                    {f.label}
                  </label>
                );
              }
              if (f.type === "textarea") {
                return (
                  <label key={f.key} className={`text-[11px] text-slate-600 ${f.w || "md:col-span-6"}`}>
                    {f.label}
                    <textarea
                      value={v || ""}
                      onChange={(e) => setRow(i, f.key, e.target.value)}
                      data-testid={`${testidPrefix}-${i}-${f.key}`}
                      rows={2}
                      placeholder={f.placeholder}
                      className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded"
                    />
                  </label>
                );
              }
              return (
                <label key={f.key} className={`text-[11px] text-slate-600 ${f.w || "md:col-span-2"}`}>
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                  <input
                    type={f.type || "text"}
                    value={v ?? ""}
                    onChange={(e) => setRow(i, f.key, f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
                    data-testid={`${testidPrefix}-${i}-${f.key}`}
                    placeholder={f.placeholder}
                    className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded"
                  />
                </label>
              );
            })}
          </div>
          <div className="flex justify-end mt-1.5">
            <button
              type="button"
              onClick={() => removeRow(i)}
              data-testid={`${testidPrefix}-remove-${i}`}
              className="text-xs text-red-600 inline-flex items-center gap-1 hover:underline"
            >
              <X className="h-3 w-3" /> Remove
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        data-testid={`${testidPrefix}-add`}
        className="text-xs px-2 py-1.5 inline-flex items-center gap-1 bg-white border border-slate-300 rounded hover:bg-slate-50"
      >
        <Plus className="h-3 w-3" /> {addLabel || "Add row"}
      </button>
    </div>
  );
}

export const MEDICATION_FIELDS = [
  { key: "name", label: "Medication", type: "text", required: true, w: "md:col-span-2" },
  { key: "dose", label: "Dose", type: "text", placeholder: "e.g. 100mcg", w: "md:col-span-1" },
  { key: "route", label: "Route", type: "select", w: "md:col-span-1",
    options: [
      { value: "oral", label: "Oral" },
      { value: "topical", label: "Topical" },
      { value: "inhaled", label: "Inhaled" },
      { value: "injection", label: "Injection" },
      { value: "sublingual", label: "Sublingual" },
      { value: "rectal", label: "Rectal" },
      { value: "nasal", label: "Nasal" },
      { value: "transdermal", label: "Transdermal" },
      { value: "other", label: "Other" },
    ] },
  { key: "frequency", label: "Frequency", type: "text", placeholder: "e.g. BD with food", w: "md:col-span-2" },
  { key: "indication", label: "Indication / what it's for", type: "text", w: "md:col-span-3" },
  { key: "prescriber", label: "Prescriber", type: "text", w: "md:col-span-2" },
  { key: "prn", label: "PRN (as required)", type: "checkbox", w: "md:col-span-1" },
  { key: "prn_protocol", label: "PRN protocol — max dose / wait period", type: "textarea", placeholder: "e.g. max 2 puffs every 4 hours", w: "md:col-span-6" },
];

export const HEALTH_CONDITION_FIELDS = [
  { key: "name", label: "Condition", type: "text", required: true, w: "md:col-span-2" },
  { key: "severity", label: "Severity", type: "select", w: "md:col-span-1",
    options: [{ value: "mild", label: "Mild" }, { value: "moderate", label: "Moderate" }, { value: "severe", label: "Severe" }] },
  { key: "diagnosed_at", label: "Diagnosed", type: "date", w: "md:col-span-1" },
  { key: "lead_clinician", label: "Lead clinician", type: "text", w: "md:col-span-2" },
  { key: "monitoring", label: "Monitoring / red-flag signs", type: "textarea", w: "md:col-span-6" },
];

export const ALLERGY_FIELDS = [
  { key: "substance", label: "Substance / trigger", type: "text", required: true, w: "md:col-span-2" },
  { key: "severity", label: "Severity", type: "select", w: "md:col-span-1",
    options: [{ value: "mild", label: "Mild" }, { value: "moderate", label: "Moderate" }, { value: "severe", label: "Severe" }, { value: "anaphylaxis", label: "Anaphylaxis" }] },
  { key: "reaction", label: "Reaction", type: "text", w: "md:col-span-3" },
];

export function EducationChildEditor({ value, onChange, testidPrefix }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded border border-slate-200 p-2.5 bg-slate-50" data-testid={`${testidPrefix}-edu-child`}>
      <label className="text-[11px] text-slate-600">School name
        <input value={v.school_name || ""} onChange={(e) => set("school_name", e.target.value)} data-testid={`${testidPrefix}-school-name`} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
      <label className="text-[11px] text-slate-600">School phone
        <input value={v.school_phone || ""} onChange={(e) => set("school_phone", e.target.value)} data-testid={`${testidPrefix}-school-phone`} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
      <label className="text-[11px] text-slate-600">Year group
        <input value={v.year_group || ""} onChange={(e) => set("year_group", e.target.value)} data-testid={`${testidPrefix}-year-group`} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
      <label className="text-[11px] text-slate-600">Key teacher / DSL
        <input value={v.key_teacher || ""} onChange={(e) => set("key_teacher", e.target.value)} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
      <label className="text-[11px] text-slate-600">SEN status
        <select value={v.sen_status || ""} onChange={(e) => set("sen_status", e.target.value)} data-testid={`${testidPrefix}-sen-status`} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white">
          <option value="">—</option>
          <option value="no_sen">No SEN</option>
          <option value="sen_support">SEN Support</option>
          <option value="ehcp">EHCP</option>
        </select>
      </label>
      <label className="text-[11px] text-slate-600">EHCP reference
        <input value={v.ehcp_ref || ""} onChange={(e) => set("ehcp_ref", e.target.value)} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
      <label className="text-[11px] text-slate-600">Personal Education Plan ref
        <input value={v.pep_ref || ""} onChange={(e) => set("pep_ref", e.target.value)} data-testid={`${testidPrefix}-pep-ref`} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
      <label className="text-[11px] text-slate-600">Attendance %
        <input type="number" min="0" max="100" step="0.1" value={v.attendance_pct ?? ""} onChange={(e) => set("attendance_pct", e.target.value === "" ? null : Number(e.target.value))} className="mt-0.5 w-full px-2 py-1 text-xs border border-slate-300 rounded" />
      </label>
    </div>
  );
}
